/**
 * Cles SSH <-> PKCS#8 : la forme que le FIDO CXF v1.0 impose au credential `ssh-key` (`privateKey` = PKCS#8 DER en
 * base64url, `keyType` = algorithme SSH). C'est elle que Bitwarden, 1Password et les gestionnaires des OS echangent :
 * sans elle, une cle SSH partait de Klavox sans sa cle privee et arrivait chez Klavox en notes.
 *
 * Aucune arithmetique de courbe ici. Vers le CXF : la graine d'une cle OpenSSH Ed25519 est replacee derriere
 * l'en-tete PKCS#8 fixe de la RFC 8410 ; les champs d'une cle OpenSSH RSA ou ECDSA sont reecrits en DER (RFC 8017
 * `RSAPrivateKey`, RFC 5915 `ECPrivateKey`) ; une cle PEM historique (`BEGIN RSA PRIVATE KEY` / `BEGIN EC PRIVATE KEY`,
 * le format d'`ssh-keygen` avant OpenSSH 7.8) est enveloppee telle quelle dans PKCS#8 (RFC 5208) ; un bloc
 * `-----BEGIN PRIVATE KEY-----` est recopie tel quel. Le seul calcul est RSA : PKCS#1 exige `dp = d mod (p-1)` et
 * `dq = d mod (q-1)`, qu'OpenSSH ne stocke pas (deux restes de division, comme `ssh-keygen -m PKCS8`), apres deux
 * controles de coherence (`p*q = n`, `iqmp*q = 1 mod p`). Depuis le CXF :
 * c'est WebCrypto qui lit le PKCS#8 (`importKey`) et rend la cle publique (`exportKey('jwk')`) ; Ed25519 est remise en
 * `openssh-key-v1` (l'agent `klavox ssh agent` la sert), RSA et ECDSA restent en PKCS#8 (que `ssh` lit tel quel).
 * Les tampons de cle privee intermediaires sont effaces avant de rendre la main (les `bigint` de la branche RSA sont
 * immuables : ils ne peuvent pas l'etre, ils sont laisses au ramasse-miettes).
 */
import { base64ToBytes, base64UrlToBytes, bytesToBase64, bytesToBase64Url } from '@klavox/crypto-core';
import {
  SshReader,
  checkSshKey,
  normalizePrivateKey,
  parseSshPublicKey,
  readOpenSshEd25519,
  readOpenSshPrivateKey,
  type SshKeyMaterial,
  type SshPublicKeyInfo,
} from './ssh-key-item.js';
import { PKCS8_ED25519_PREFIX, concat, openSshEd25519Key, sshString } from './ssh-keygen.js';

/** Cle privee prete pour le CXF : PKCS#8 DER et algorithme SSH. */
export interface SshPkcs8 {
  readonly keyType: string;
  readonly der: Uint8Array;
  readonly comment: string;
}

const PKCS8_BLOCK = /^-----BEGIN PRIVATE KEY-----\n([A-Za-z0-9+/=\n]+)\n-----END PRIVATE KEY-----$/u;

/** Bloc PEM PKCS#8 non chiffre (`-----BEGIN PRIVATE KEY-----`), 64 colonnes comme OpenSSL. */
export function pkcs8Pem(der: Uint8Array): string {
  return `-----BEGIN PRIVATE KEY-----\n${bytesToBase64(der).replace(/(.{64})/gu, '$1\n').replace(/\n$/u, '')}\n-----END PRIVATE KEY-----\n`;
}

/** Corps DER d'un bloc PKCS#8 non chiffre, ou `null`. */
function pemPkcs8Der(text: string): Uint8Array | null {
  const match = PKCS8_BLOCK.exec(normalizePrivateKey(text).trimEnd());
  if (match === null) return null;
  try {
    return base64ToBytes(match[1]!.split('\n').join(''));
  } catch {
    return null;
  }
}

const sameBytes = (a: Uint8Array, b: Uint8Array): boolean => a.length === b.length && a.every((byte, i) => byte === b[i]);

/** Entier non signe big-endian sans zeros de tete (`mpint` SSH positif, INTEGER DER positif). */
function unsigned(bytes: Uint8Array): Uint8Array {
  let start = 0;
  while (start < bytes.length - 1 && bytes[start] === 0) start += 1;
  return bytes.subarray(start);
}

function toBigInt(bytes: Uint8Array): bigint {
  let value = 0n;
  for (const byte of bytes) value = (value << 8n) | BigInt(byte);
  return value;
}

function fromBigInt(value: bigint): Uint8Array {
  const hex = value.toString(16);
  return Uint8Array.from((hex.length % 2 === 0 ? hex : `0${hex}`).match(/../gu)!, (pair) => Number.parseInt(pair, 16));
}

function derLength(length: number): Uint8Array {
  if (length < 0x80) return Uint8Array.of(length);
  const bytes: number[] = [];
  for (let rest = length; rest > 0; rest = Math.floor(rest / 256)) bytes.unshift(rest % 256);
  return Uint8Array.of(0x80 | bytes.length, ...bytes);
}

/** TLV DER ; le contenu intermediaire est efface (il porte de la cle privee). */
function der(tag: number, ...content: Uint8Array[]): Uint8Array {
  const body = concat(content);
  try {
    return concat([Uint8Array.of(tag), derLength(body.length), body]);
  } finally {
    body.fill(0);
  }
}

/** INTEGER DER positif depuis un entier non signe big-endian. */
function derUnsigned(bytes: Uint8Array): Uint8Array {
  const value = unsigned(bytes);
  return der(0x02, (value[0]! & 0x80) !== 0 ? concat([Uint8Array.of(0), value]) : value);
}

/** TLV DER enfants d'un contenu, ou `null` des qu'une longueur deborde. */
function derChildren(bytes: Uint8Array): { readonly tag: number; readonly value: Uint8Array }[] | null {
  const children: { tag: number; value: Uint8Array }[] = [];
  let offset = 0;
  while (offset < bytes.length) {
    if (offset + 2 > bytes.length) return null;
    const tag = bytes[offset]!;
    let length = bytes[offset + 1]!;
    offset += 2;
    if ((length & 0x80) !== 0) {
      const size = length & 0x7f;
      if (size === 0 || size > 3 || offset + size > bytes.length) return null;
      length = 0;
      for (let i = 0; i < size; i += 1) length = length * 256 + bytes[offset + i]!;
      offset += size;
    }
    if (offset + length > bytes.length) return null;
    children.push({ tag, value: bytes.subarray(offset, offset + length) });
    offset += length;
  }
  return children;
}

/** Contenu de l'unique SEQUENCE d'un DER, decoupe en enfants. */
function derSequence(bytes: Uint8Array): { readonly tag: number; readonly value: Uint8Array }[] | null {
  const outer = derChildren(bytes);
  return outer?.length === 1 && outer[0]!.tag === 0x30 ? derChildren(outer[0]!.value) : null;
}

const DER_VERSION_0 = Uint8Array.of(0x02, 0x01, 0x00);
const DER_VERSION_1 = Uint8Array.of(0x02, 0x01, 0x01);
/** AlgorithmIdentifier `rsaEncryption` (1.2.840.113549.1.1.1), parametre NULL. */
const DER_RSA_ALGORITHM = Uint8Array.from([0x30, 0x0d, 0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01, 0x05, 0x00]);
/** OID `id-ecPublicKey` (1.2.840.10045.2.1, RFC 5480). */
const DER_EC_PUBLIC_KEY = Uint8Array.from([0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01]);

/** PKCS#8 (RFC 5208) : version 0, algorithme, cle privee dans un OCTET STRING. */
function pkcs8Of(algorithm: Uint8Array, privateKey: Uint8Array): Uint8Array {
  return der(0x30, DER_VERSION_0, algorithm, der(0x04, privateKey));
}

/** Les facteurs vont avec le module et `iqmp` est l'inverse de q modulo p : sinon la cle est alteree. */
function rsaCoherent(n: bigint, d: bigint, p: bigint, q: bigint, iqmp: bigint): boolean {
  return p > 1n && q > 1n && d > 0n && p * q === n && (iqmp * q) % p === 1n;
}

/** `e` et `n` d'un blob de cle publique `ssh-rsa`. */
function rsaPublic(blob: Uint8Array): { readonly e: Uint8Array; readonly n: Uint8Array } | null {
  const reader = new SshReader(blob);
  reader.string();
  const e = reader.string();
  const n = reader.string();
  return e === null || n === null || !reader.done() ? null : { e: unsigned(e), n: unsigned(n) };
}

/** Courbe et point Q d'un blob de cle publique `ecdsa-sha2-*`. */
function ecdsaPublic(blob: Uint8Array): { readonly curve: string; readonly point: Uint8Array } | null {
  const reader = new SshReader(blob);
  reader.string();
  const curve = reader.string();
  const point = reader.string();
  return curve === null || point === null || !reader.done() ? null : { curve: new TextDecoder().decode(curve), point };
}

type EcCurve = (typeof EC_CURVES)[number];

/** PKCS#8 d'une cle ECDSA : `ECPrivateKey` (RFC 5915) au scalaire de longueur fixe, point Q joint. */
function ecdsaPkcs8(curve: EcCurve, scalar: Uint8Array, point: Uint8Array): Uint8Array | null {
  const d = unsigned(scalar);
  if (d.length > curve.size || (d.length === 1 && d[0] === 0)) return null;
  if (point.length !== 1 + 2 * curve.size || point[0] !== 4) return null;
  const fixed = new Uint8Array(curve.size);
  fixed.set(d, curve.size - d.length);
  const ecKey = der(0x30, DER_VERSION_1, der(0x04, fixed), der(0xa1, der(0x03, Uint8Array.of(0), point)));
  fixed.fill(0);
  try {
    return pkcs8Of(der(0x30, DER_EC_PUBLIC_KEY, der(0x06, Uint8Array.from(curve.oid))), ecKey);
  } finally {
    ecKey.fill(0);
  }
}

/** PKCS#8 d'une cle OpenSSH RSA ou ECDSA non chiffree (champs de `readOpenSshPrivateKey`), ou `null`. */
function openSshToPkcs8(algorithm: string, publicBlob: Uint8Array, fields: readonly Uint8Array[]): Uint8Array | null {
  if (algorithm === 'ssh-rsa') {
    // Ordre OpenSSH : n, e, d, iqmp, p, q.
    const [n, e, d, iqmp, p, q] = fields.map(unsigned) as [Uint8Array, Uint8Array, Uint8Array, Uint8Array, Uint8Array, Uint8Array];
    const header = rsaPublic(publicBlob);
    if (header === null || !sameBytes(header.n, n) || !sameBytes(header.e, e)) return null;
    const [bn, bd, bp, bq, biqmp] = [n, d, p, q, iqmp].map(toBigInt) as [bigint, bigint, bigint, bigint, bigint];
    if (!rsaCoherent(bn, bd, bp, bq, biqmp)) return null;
    // `RSAPrivateKey` (RFC 8017 A.1.2) : version, n, e, d, p, q, d mod (p-1), d mod (q-1), q^-1 mod p.
    const dp = fromBigInt(bd % (bp - 1n));
    const dq = fromBigInt(bd % (bq - 1n));
    const rsaKey = der(0x30, DER_VERSION_0, ...[n, e, d, p, q, dp, dq, iqmp].map(derUnsigned));
    dp.fill(0);
    dq.fill(0);
    try {
      return pkcs8Of(DER_RSA_ALGORITHM, rsaKey);
    } finally {
      rsaKey.fill(0);
    }
  }
  const curve = EC_CURVES.find((candidate) => algorithm === `ecdsa-sha2-${candidate.ssh}`);
  const [name, point, scalar] = fields;
  const header = ecdsaPublic(publicBlob);
  if (curve === undefined || name === undefined || point === undefined || scalar === undefined || header === null) return null;
  // Le point prive doit etre celui de l'en-tete, sur la courbe annoncee.
  if (new TextDecoder().decode(name) !== curve.ssh || header.curve !== curve.ssh || !sameBytes(header.point, point)) return null;
  return ecdsaPkcs8(curve, scalar, point);
}

const LEGACY_BLOCK = /^-----BEGIN (RSA|EC) PRIVATE KEY-----\n([A-Za-z0-9+/=\n]+)\n-----END \1 PRIVATE KEY-----$/u;

/**
 * PKCS#8 d'une cle PEM historique NON chiffree (`BEGIN RSA PRIVATE KEY` = PKCS#1, `BEGIN EC PRIVATE KEY` = SEC1),
 * format d'`ssh-keygen` avant OpenSSH 7.8 et de `ssh-keygen -m PEM`. Une cle chiffree (`Proc-Type`) ne passe pas
 * l'expression. La ligne publique, si elle est la, doit etre celle de la cle.
 */
function legacyPemToPkcs8(text: string, publicInfo: SshPublicKeyInfo | null): { readonly keyType: string; readonly der: Uint8Array } | null {
  const match = LEGACY_BLOCK.exec(normalizePrivateKey(text).trimEnd());
  if (match === null) return null;
  let body: Uint8Array;
  try {
    body = base64ToBytes(match[2]!.split('\n').join(''));
  } catch {
    return null;
  }
  try {
    const children = derSequence(body);
    if (children === null || children.some((child) => child.value.length === 0)) return null;
    if (match[1] === 'RSA') {
      // `RSAPrivateKey` a deux facteurs : version 0 puis huit INTEGER.
      if (children.length !== 9 || children.some((child) => child.tag !== 0x02) || !sameBytes(children[0]!.value, Uint8Array.of(0))) return null;
      const [n, e, d, p, q, , , iqmp] = children.slice(1).map((child) => unsigned(child.value)) as Uint8Array[];
      if (!rsaCoherent(toBigInt(n!), toBigInt(d!), toBigInt(p!), toBigInt(q!), toBigInt(iqmp!))) return null;
      if (publicInfo !== null) {
        const line = publicInfo.algorithm === 'ssh-rsa' ? rsaPublic(publicInfo.blob) : null;
        if (line === null || !sameBytes(line.n, n!) || !sameBytes(line.e, e!)) return null;
      }
      return { keyType: 'ssh-rsa', der: pkcs8Of(DER_RSA_ALGORITHM, body) };
    }
    // `ECPrivateKey` : version 1, scalaire, [0] courbe (optionnelle), [1] point Q (optionnel).
    const [version, scalar, ...tagged] = children;
    if (version?.tag !== 0x02 || !sameBytes(version.value, Uint8Array.of(1)) || scalar?.tag !== 0x04) return null;
    const parameters = tagged.find((child) => child.tag === 0xa0);
    const publicKey = tagged.find((child) => child.tag === 0xa1);
    const oid = parameters === undefined ? null : derChildren(parameters.value);
    const line = publicInfo !== null && publicInfo.algorithm.startsWith('ecdsa-sha2-') ? ecdsaPublic(publicInfo.blob) : null;
    if (publicInfo !== null && line === null) return null;
    const curve = EC_CURVES.find((candidate) =>
      oid !== null ? oid.length === 1 && oid[0]!.tag === 0x06 && sameBytes(oid[0]!.value, Uint8Array.from(candidate.oid)) : candidate.ssh === line?.curve,
    );
    if (curve === undefined || (line !== null && line.curve !== curve.ssh)) return null;
    const bits = publicKey === undefined ? null : derChildren(publicKey.value);
    const embedded = bits?.length === 1 && bits[0]!.tag === 0x03 && bits[0]!.value[0] === 0 ? bits[0]!.value.subarray(1) : null;
    if (publicKey !== undefined && embedded === null) return null;
    if (embedded !== null && line !== null && !sameBytes(embedded, line.point)) return null;
    const point = embedded ?? line?.point;
    if (point === undefined) return null;
    const ecDer = ecdsaPkcs8(curve, scalar.value, point);
    return ecDer === null ? null : { keyType: `ecdsa-sha2-${curve.ssh}`, der: ecDer };
  } finally {
    body.fill(0);
  }
}

/**
 * Cle privee de l'entree en PKCS#8, ou `null` quand le CXF ne peut pas la porter nativement (cle chiffree par une
 * phrase de passe, DSA, cle materielle `sk-`, cle alteree, ligne publique qui n'est pas celle de la cle, cle
 * publique seule) : l'appelant la transporte alors autrement, jamais en la perdant.
 */
export function sshKeyToPkcs8(material: SshKeyMaterial): SshPkcs8 | null {
  // Une cle chiffree par phrase de passe (OpenSSH `aes256-ctr`, `ENCRYPTED PRIVATE KEY`, PEM `Proc-Type`) n'est lue
  // par aucune des branches ci-dessous : elle part par le repli de l'appelant.
  if (material.privateKey.length === 0) return null;
  // Ligne publique d'une autre cle que la cle privee OpenSSH : l'autre gestionnaire afficherait une identite et
  // signerait avec une autre.
  if (checkSshKey(material) === 'mismatch') return null;
  const publicInfo = material.publicKey.length > 0 ? parseSshPublicKey(material.publicKey) : null;
  const ed25519 = readOpenSshEd25519(material.privateKey);
  if (ed25519 !== null) {
    const der = concat([PKCS8_ED25519_PREFIX, ed25519.seed]);
    ed25519.seed.fill(0);
    return { keyType: 'ssh-ed25519', der, comment: publicInfo?.comment || ed25519.comment };
  }
  const openSsh = readOpenSshPrivateKey(material.privateKey);
  if (openSsh !== null) {
    try {
      const converted = openSshToPkcs8(openSsh.algorithm, openSsh.publicBlob, openSsh.fields);
      return converted === null ? null : { keyType: openSsh.algorithm, der: converted, comment: publicInfo?.comment || openSsh.comment };
    } finally {
      for (const field of openSsh.fields) field.fill(0);
    }
  }
  const legacy = legacyPemToPkcs8(material.privateKey, publicInfo);
  if (legacy !== null) return { ...legacy, comment: publicInfo?.comment ?? '' };
  // PKCS#8 deja la : recopie, l'algorithme vient de la ligne publique (le DER ne dit pas « ssh-rsa »).
  const der = pemPkcs8Der(material.privateKey);
  if (der === null || publicInfo === null) return null;
  return { keyType: publicInfo.algorithm, der, comment: publicInfo.comment };
}

/** base64url avec ou sans remplissage (la spec CXF et les JWK l'omettent, le decodeur l'exige). */
function fromBase64Url(text: string): Uint8Array {
  const bare = text.replace(/=+$/u, '');
  return base64UrlToBytes(bare + '='.repeat((4 - (bare.length % 4)) % 4));
}

/** base64url SANS remplissage, comme le veut la spec CXF (`B64url`). */
export function toCxfBase64Url(bytes: Uint8Array): string {
  return bytesToBase64Url(bytes).replace(/=+$/u, '');
}

/** PKCS#8 DER d'un credential CXF : base64url selon la spec, base64 classique tolere (emetteurs constates). */
export function decodeCxfPrivateKey(value: string): Uint8Array | null {
  const compact = value.replace(/\s+/gu, '');
  if (!/^[A-Za-z0-9+/_-]+={0,2}$/u.test(compact)) return null;
  try {
    const der = fromBase64Url(compact.replace(/\+/gu, '-').replace(/\//gu, '_'));
    // Un PKCS#8 est une SEQUENCE DER : tout autre premier octet n'est pas une cle.
    return der.length > 16 && der[0] === 0x30 ? der : null;
  } catch {
    return null;
  }
}

/** Entier positif SSH (`mpint`, RFC 4251 §5) depuis un entier non signe big-endian (JWK). */
function mpint(unsigned: Uint8Array): Uint8Array {
  let start = 0;
  while (start < unsigned.length - 1 && unsigned[start] === 0) start += 1;
  const trimmed = unsigned.subarray(start);
  return sshString((trimmed[0]! & 0x80) !== 0 ? concat([Uint8Array.of(0), trimmed]) : trimmed);
}

function publicLine(algorithm: string, blob: Uint8Array, comment: string): string {
  const line = `${algorithm} ${bytesToBase64(blob)}`;
  return comment.length > 0 ? `${line} ${comment}` : line;
}

/** Courbes ECDSA d'OpenSSH : nom WebCrypto, nom SSH, taille du scalaire, OID (RFC 5480). */
const EC_CURVES = [
  { namedCurve: 'P-256', ssh: 'nistp256', size: 32, oid: [0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07] },
  { namedCurve: 'P-384', ssh: 'nistp384', size: 48, oid: [0x2b, 0x81, 0x04, 0x00, 0x22] },
  { namedCurve: 'P-521', ssh: 'nistp521', size: 66, oid: [0x2b, 0x81, 0x04, 0x00, 0x23] },
] as const;

async function importJwk(subtle: SubtleCrypto, der: Uint8Array, algorithm: AlgorithmIdentifier | EcKeyImportParams | RsaHashedImportParams): Promise<JsonWebKey | null> {
  // Tampon non partage (`BufferSource` exclut `SharedArrayBuffer`), efface apres usage.
  const copy = new Uint8Array(der);
  try {
    const key = await subtle.importKey('pkcs8', copy, algorithm, true, ['sign']);
    return await subtle.exportKey('jwk', key);
  } catch {
    return null;
  } finally {
    copy.fill(0);
  }
}

/**
 * Paire d'entree `ssh-key` depuis le PKCS#8 d'un credential CXF : cle publique rendue par WebCrypto, Ed25519 remise
 * en `openssh-key-v1`. `null` si WebCrypto ne lit le DER sous aucun algorithme SSH (cle illisible, ou navigateur sans
 * Ed25519) : l'appelant garde alors le PKCS#8 tel quel.
 */
export async function sshKeyFromPkcs8(
  der: Uint8Array,
  comment: string,
  subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle,
): Promise<SshKeyMaterial | null> {
  if (subtle === undefined) return null;
  const line = comment.replace(/\s+/gu, ' ').trim();
  const ed = await importJwk(subtle, der, { name: 'Ed25519' });
  if (ed?.d !== undefined && ed.x !== undefined) {
    const seed = fromBase64Url(ed.d);
    try {
      return openSshEd25519Key(seed, fromBase64Url(ed.x), line);
    } finally {
      seed.fill(0);
    }
  }
  for (const curve of EC_CURVES) {
    const ec = await importJwk(subtle, der, { name: 'ECDSA', namedCurve: curve.namedCurve });
    if (ec?.x === undefined || ec.y === undefined) continue;
    const algorithm = `ecdsa-sha2-${curve.ssh}`;
    const point = concat([Uint8Array.of(4), fromBase64Url(ec.x), fromBase64Url(ec.y)]);
    return { privateKey: pkcs8Pem(der), publicKey: publicLine(algorithm, concat([sshString(algorithm), sshString(curve.ssh), sshString(point)]), line) };
  }
  const rsa = await importJwk(subtle, der, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' });
  if (rsa?.n !== undefined && rsa.e !== undefined) {
    const blob = concat([sshString('ssh-rsa'), mpint(fromBase64Url(rsa.e)), mpint(fromBase64Url(rsa.n))]);
    return { privateKey: pkcs8Pem(der), publicKey: publicLine('ssh-rsa', blob, line) };
  }
  return null;
}
